import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import Sidebar from './Sidebar';
import BottomNav from './BottomNav';
import Footer from './Footer';
import InstallPrompt from './InstallPrompt';
import Wordmark from './shell/Wordmark';
import Avatar from './shell/Avatar';
import ThemeToggle from './shell/ThemeToggle';
import { MARKETING_NAV, MARKETING_MORE, isActivePath } from './shell/nav';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../hooks/useTheme';

// The one shell for every page (2026-10 restyle, after linear.app).
//
//   ≥1024px  248px sticky left rail · content column
//   <1024px  sticky translucent header · content · (signed in) bottom tab bar
//
// Signed in, the rail holds the six app pages + account/theme; signed out it
// holds the marketing pages + Sign up / Log in. Phones get the same two sets
// through the tab bar (app) or a slide-in drawer behind "Menu" (marketing).

const Layout = ({ children }) => {
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { pathname } = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);


  // Close the marketing drawer whenever the route changes.
  useEffect(() => { setDrawerOpen(false); }, [pathname]);

  // Smooth scrolling for in-page anchor links.
  useEffect(() => {
    const handleSmoothScroll = (e) => {
      const target = e.target.closest('a');
      if (target && target.getAttribute('href')?.startsWith('#')) {
        e.preventDefault();
        const el = document.querySelector(target.getAttribute('href'));
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    };
    document.addEventListener('click', handleSmoothScroll);
    return () => document.removeEventListener('click', handleSmoothScroll);
  }, []);

  // Content column: the mock's 24px / 40px top breathing room, plus clearance
  // for the phone tab bar when signed in.
  const mainPad = `pt-6 lg:pt-10 ${user ? 'pb-[calc(84px+env(safe-area-inset-bottom))] lg:pb-0' : ''}`;

  return (
    <div className="min-h-screen bg-canvas text-fg lg:grid lg:grid-cols-[248px_minmax(0,1fr)]">
      <Sidebar theme={theme} toggleTheme={toggleTheme} />

      <div className="min-w-0 flex flex-col min-h-screen">
        {/* Phone header */}
        <header
          className="lg:hidden sticky z-40 h-14 px-4 flex items-center justify-between chrome-glass border-b border-line"
          style={{ top: 'env(safe-area-inset-top, 0px)' }}
        >
          <div className="flex items-center gap-4">
            {!user && (
              <button
                type="button"
                onClick={() => setDrawerOpen(true)}
                className="inline-flex items-center gap-2 py-1.5 text-sm font-medium text-fg-2 hover:text-fg"
                aria-label="Open menu"
                aria-expanded={drawerOpen}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" className="w-5 h-5" aria-hidden="true">
                  <path d="M4 7h16M4 12h16M4 17h16" />
                </svg>
                Menu
              </button>
            )}
            <Wordmark to={user ? '/home' : '/'} />
          </div>
          <div className="flex items-center gap-2">
            {user ? (
              <>
                <ThemeToggle theme={theme} onToggle={toggleTheme} />
                <Link to="/me" aria-label="Profile" className="rounded-full">
                  <Avatar user={user} size={36} />
                </Link>
              </>
            ) : (
              <>
                <Link to="/login" className="hidden sm:inline-block px-3 py-2 rounded-full text-sm font-medium text-fg-2 hover:text-fg">Log in</Link>
                <Link to="/register" className="pill-cta">Sign up</Link>
                <ThemeToggle theme={theme} onToggle={toggleTheme} />
              </>
            )}
          </div>
        </header>

        <main className={`flex-1 min-w-0 ${mainPad}`}>
          {children}
        </main>

        <Footer variant={user ? 'app' : 'marketing'} />
      </div>

      {user && <BottomNav />}

      {/* Marketing drawer (phones, signed out) */}
      {!user && drawerOpen && (
        <div className="lg:hidden fixed inset-0 z-50" role="dialog" aria-modal="true" aria-label="Menu">
          <button type="button" className="absolute inset-0 bg-black/50" aria-label="Close menu" onClick={() => setDrawerOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-72 max-w-[85vw] bg-canvas border-r border-line flex flex-col p-4 pt-5">
            <div className="flex items-center justify-between px-2.5 pb-5">
              <Wordmark to="/" />
              <button type="button" onClick={() => setDrawerOpen(false)} className="tgl" aria-label="Close menu">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
              </button>
            </div>
            <nav className="flex flex-col gap-0.5" aria-label="Menu">
              {MARKETING_NAV.map((item) => (
                <Link key={item.to} to={item.to} className={`px-2.5 py-2 rounded-lg text-base font-medium ${isActivePath(pathname, item.to) ? 'text-fg bg-raised' : 'text-fg-2 hover:text-fg hover:bg-raised'}`}>
                  {item.label}
                </Link>
              ))}
              <div className="my-2 border-t border-line" />
              {MARKETING_MORE.map((item) => (
                <Link key={item.to} to={item.to} className={`px-2.5 py-1.5 rounded-lg text-sm ${isActivePath(pathname, item.to) ? 'text-fg bg-raised' : 'text-fg-3 hover:text-fg hover:bg-raised'}`}>
                  {item.label}
                </Link>
              ))}
            </nav>
            <div className="mt-auto flex flex-col gap-2 border-t border-line pt-3.5">
              <Link to="/register" className="pill-cta w-full">Sign up</Link>
              <Link to="/login" className="pill-ghost w-full">Log in</Link>
            </div>
          </div>
        </div>
      )}

      <InstallPrompt />

      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: 'var(--bg-elev)',
            color: 'var(--fg)',
            border: '1px solid var(--line-strong)',
            borderRadius: '12px',
            fontSize: '0.95rem',
          },
          success: { duration: 3000, iconTheme: { primary: 'var(--good)', secondary: 'var(--bg-elev)' } },
          error:   { duration: 4000, iconTheme: { primary: 'var(--off)',  secondary: 'var(--bg-elev)' } },
        }}
      />
    </div>
  );
};

export default Layout;
